import { Download, FileDown, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import {
  DEFAULT_ORG_ANALYTICS_PDF_SECTION_IDS,
  ORG_ANALYTICS_PDF_SECTIONS,
  type OrgAnalyticsPdfSectionId,
} from '@/pages/analytics/utils/orgPdfExportSections';

interface OrgPdfExportMenuProps {
  disabled?: boolean;
  onExport: (sectionIds: OrgAnalyticsPdfSectionId[]) => void | Promise<void>;
}

const ALL_SECTION_IDS = DEFAULT_ORG_ANALYTICS_PDF_SECTION_IDS;

export const OrgPdfExportMenu: React.FC<OrgPdfExportMenuProps> = ({ disabled, onExport }) => {
  const [open, setOpen] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] =
    useState<OrgAnalyticsPdfSectionId[]>(ALL_SECTION_IDS);
  const [isExporting, setIsExporting] = useState(false);
  const selectedCount = selectedSectionIds.length;
  const allSectionsSelected = selectedCount === ALL_SECTION_IDS.length;
  const selectedSet = new Set(selectedSectionIds);

  const toggleSection = (sectionId: OrgAnalyticsPdfSectionId, checked: boolean) => {
    setSelectedSectionIds(current =>
      checked
        ? current.includes(sectionId)
          ? current
          : [...current, sectionId]
        : current.filter(id => id !== sectionId),
    );
  };

  const handleExport = async () => {
    if (disabled || isExporting || selectedCount === 0) return;

    setIsExporting(true);
    try {
      await onExport(selectedSectionIds);
      setOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export PDF';
      toast.error(message);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleAllSections = () => {
    setSelectedSectionIds(allSectionsSelected ? [] : ALL_SECTION_IDS);
  };

  const menu = (
    <Popover open={open} onOpenChange={nextOpen => !isExporting && setOpen(nextOpen)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || isExporting}
          aria-label="Download organization performance as PDF"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 overflow-hidden p-0">
        <div className="flex items-center justify-between gap-3 border-b px-3 py-2.5">
          <h3 className="text-sm font-medium leading-5 text-foreground">Export as PDF</h3>
          <button
            type="button"
            className="text-xs font-medium leading-5 text-primary hover:text-primary/80 disabled:pointer-events-none disabled:opacity-50"
            onClick={toggleAllSections}
            disabled={isExporting}
          >
            {allSectionsSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>

        <div className="py-1.5">
          {ORG_ANALYTICS_PDF_SECTIONS.map(section => {
            const checked = selectedSet.has(section.id);
            const sectionInputId = `org-pdf-export-${section.id}`;

            return (
              <div
                key={section.id}
                className="flex items-center gap-2.5 px-3 py-1.5 text-sm leading-5 text-foreground hover:bg-muted"
              >
                <Checkbox
                  id={sectionInputId}
                  checked={checked}
                  onCheckedChange={nextChecked => toggleSection(section.id, nextChecked === true)}
                  disabled={isExporting}
                  className="h-4 w-4 data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                />
                <label
                  htmlFor={sectionInputId}
                  className="min-w-0 flex-1 cursor-pointer truncate font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {section.label}
                </label>
              </div>
            );
          })}
        </div>

        <div className="border-t p-2.5">
          <Button
            type="button"
            size="sm"
            className="w-full gap-2"
            disabled={disabled || isExporting || selectedCount === 0}
            onClick={handleExport}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Export PDF ({selectedCount})
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );

  if (open || isExporting) return menu;

  return (
    <SimpleTooltipV2 content="Download organization performance" side="top">
      {menu}
    </SimpleTooltipV2>
  );
};
